import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { debounce } from "~/utils/debounce";
import { isVisibleMaker } from "~/utils/isVisibleMaker";
import { slugify } from "~/utils/slugify";
import { getErrorStatus, handleInvalidToken } from "~/utils/apiError";
import type { Maker, MakerCategory } from "~/types";
import type { CurationMenuResponse } from "~/types/menuRules";

// Re-export shared types for consumers that already import from this store
export type { Maker, MakerCategory };

// The maker-detail endpoint returns the maker with its menu inlined. Named so
// getMakerBySlug can declare it as a return type and callers keep full typing.
export type MakerWithCategories = Maker & { productCategories?: unknown[] };

export interface MakersMap {
  byId: Record<string | number, Maker>;
  bySlug: Record<string, Maker>;
}

export interface DeepFilterResponse {
  ids: Array<string | number>;
  cuisineRank: Record<string, number>;
  total: number;
}

export const useMakersStore = defineStore("makers", () => {
  const authStore = useAuthStore();
  const config = useRuntimeConfig();
  const { fetchAttachments } = useAttachments();
  const { runDeepSearch } = useDeepSearch();

  // --------------- State ---------------
  const makers = ref<Maker[] | false>(false);
  const makersMap = ref<MakersMap>({ byId: {}, bySlug: {} });
  const makerCategories = ref<MakerCategory[] | false>(false);
  const categoryMakersById = computed<Record<string | number, Maker>>(() => {
    if (!Array.isArray(makerCategories.value)) return {};
    const map: Record<string | number, Maker> = Object.create(null);
    for (const cat of makerCategories.value) {
      for (const maker of cat.makers || []) {
        if (!(maker.id in map)) map[maker.id] = maker;
      }
    }
    return map;
  });
  const chosenMaker = ref<Maker | false>(false);
  const chosenMakerCategory = ref<MakerCategory | false>(false);
  const makersLoading = ref(true);
  const makerCategoriesLoading = ref(true);
  // Shared in-flight promise for `getMakerCategories`. Deliberately a plain
  // variable, not a ref — it is request plumbing, not rendered state, and it
  // must not be serialised into the SSR payload.
  let makerCategoriesRequest: Promise<void> | null = null;
  const makerLoading = ref(false);
  const makerDeliveryDays = ref<unknown[] | false>(false);
  const makerIdsByDate = ref<(string | number)[]>([]);
  const validSubscriptionMakers = ref<Maker[]>([]);
  const filteredMakerCategories = ref<Maker[] | false>(false);
  // The latest deep-search results (empty = no active search). When a search is
  // active, occasion/cuisine/date filters refine THESE makers rather than the
  // full catalogue — see setFilteredMakers / setAvailableMakers.
  const searchResultMakers = ref<Maker[]>([]);
  const searchResultMakersById = computed<Record<string | number, Maker>>(() => {
    const map: Record<string | number, Maker> = Object.create(null);
    for (const maker of searchResultMakers.value) {
      if (!(maker.id in map)) map[maker.id] = maker;
    }
    return map;
  });
  const chosenPackage = ref<Record<string, unknown> | false>(false);
  const packages = ref<Record<string, unknown>[] | false>(false);
  const packagesLoading = ref(false);
  const packageTypes = ref<unknown[] | false>(false);
  const orgPackageChoices = ref<unknown | false>(false);
  const defaultPackageChoices = ref<unknown | false>(false);

  // --------------- Getters ---------------
  const openMakers = computed(() => {
    if (!makers.value) return [];
    return makers.value.filter(
      (maker) => maker.is_active && maker.makersCategories?.some((cat) => cat.is_active && cat.biz),
    );
  });

  const closedMakers = computed(() => {
    if (!makers.value) return [];
    return makers.value.filter(
      (maker) =>
        !maker.is_active &&
        maker.attachments?.logo &&
        maker.attachments?.profile &&
        maker.makersCategories?.some((cat) => cat.is_active && cat.biz),
    );
  });

  // --------------- Setters ---------------
  function setMakersLoading(val: boolean) {
    makersLoading.value = val;
  }
  function setMakerLoading(val: boolean) {
    makerLoading.value = val;
  }

  function setMakers(payload: Maker[]) {
    const userEmail = authStore.user?.email || "";
    const visible = payload.filter((m) => isVisibleMaker(m.id, userEmail));
    makers.value = visible;
    makersMap.value = visible.reduce<MakersMap>(
      (map, maker) => ({
        byId: { ...map.byId, [maker.id]: maker },
        bySlug: maker.slug ? { ...map.bySlug, [maker.slug]: maker } : map.bySlug,
      }),
      { byId: {}, bySlug: {} },
    );
  }

  function setMakerCategories(payload: MakerCategory[]) {
    const userEmail = authStore.user?.email || "";
    // Category order follows the backend `sortingIndex` (set from the Hub's
    // Homepage Settings drag-reorder) — do NOT shuffle categories here.
    // Makers within a category are sorted A–Z for stable SSR HTML.
    makerCategories.value = payload
      .map((cat) => ({
        ...cat,
        slug: cat.slug || slugify(cat.name),
        // Stable A–Z order so SSR HTML is byte-identical across requests.
        // Random shuffle defeats caching and confuses crawlers.
        makers: cat.makers
          .filter((m) => m.is_active !== false && isVisibleMaker(m.id, userEmail))
          .sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .filter((cat) => cat.biz && cat.makers.length > 0);
  }

  function setFilteredMakers(
    payload: DeepFilterResponse | false,
    cuisinesFilterApplied: boolean = false,
  ) {
    // When a search is active, the search results are the universe filters refine —
    // not the full catalogue. Mapping deepFilter's global ids over this map yields
    // searchResults ∩ filterMatches for free.
    const searchActive = searchResultMakers.value.length > 0;
    const map = searchActive ? searchResultMakersById.value : categoryMakersById.value;
    const dateSet = makerIdsByDate.value.length ? new Set(makerIdsByDate.value) : null;

    if (!payload) {
      if (searchActive) {
        // Filters cleared with a search active: restore the full search results,
        // still constrained to the selected date when one is set.
        filteredMakerCategories.value = searchResultMakers.value.filter(
          (maker) => !dateSet || dateSet.has(maker.id),
        );
        return;
      }
      filteredMakerCategories.value = dateSet
        ? makerIdsByDate.value.map((id) => map[id]).filter((maker): maker is Maker => !!maker)
        : false;
      return;
    }

    const filtered = payload.ids
      .map((id) => map[id])
      .filter((maker): maker is Maker => !!maker && (!dateSet || dateSet.has(maker.id)));

    if (cuisinesFilterApplied) {
      filtered.sort(
        (a, b) =>
          (payload.cuisineRank[String(b.id)] || 0) - (payload.cuisineRank[String(a.id)] || 0),
      );
    }

    filteredMakerCategories.value = filtered;
  }

  function setAvailableMakers(payload: (string | number)[] | false) {
    if (!payload) {
      makerIdsByDate.value = [];
      filteredMakerCategories.value = false;
      return;
    }
    makerIdsByDate.value = payload;
    // When a search is active, the date filter refines the search results;
    // otherwise it refines the full catalogue.
    const userEmail = authStore.user?.email || "";
    if (searchResultMakers.value.length > 0) {
      filteredMakerCategories.value = searchResultMakers.value.filter(
        (m) => payload.includes(m.id) && isVisibleMaker(m.id, userEmail),
      );
      return;
    }
    // Intersect with existing filtered if present
    if (makerCategories.value) {
      const allMakers = makerCategories.value.flatMap((cat) => cat.makers || []);
      const unique = [...new Map(allMakers.map((m) => [m.id, m])).values()];
      filteredMakerCategories.value = unique.filter(
        (m) => payload.includes(m.id) && isVisibleMaker(m.id, userEmail),
      );
    }
  }

  function setValidSubscriptionMakers(payload: Maker[]) {
    const userEmail = authStore.user?.email || "";
    validSubscriptionMakers.value = payload.filter((m) => isVisibleMaker(m.id, userEmail));
  }

  function setChosenPackage(payload: Record<string, unknown> | false) {
    chosenPackage.value = payload;
  }

  // --------------- Actions ---------------
  const getMakers = debounce(
    async (payload?: { filters?: Record<string, unknown>; page?: number; limit?: number }) => {
      makersLoading.value = true;

      const filtersObj = payload?.filters || {};
      const page = payload?.page || 1;
      const limit = payload?.limit || 999;
      const body = Object.keys(filtersObj).length > 0 ? { filters: filtersObj } : {};

      try {
        const response = await $fetch<{ data?: Maker[]; models?: Maker[] }>(
          `${config.public.apiBase}/api/public/makers/filter?page=${page}&limit=${limit}`,
          { method: "POST", body },
        );
        const hydrated = await hydrateMakers(response.data || response.models || []);
        makersLoading.value = false;
        setMakers(hydrated);
      } catch {
        makersLoading.value = false;
      }
    },
    0,
  );

  // List responses no longer embed maker attachments — fetch separately.
  async function hydrateMakers(makerList: Maker[]): Promise<Maker[]> {
    if (!makerList.length) return makerList;
    const ids = makerList.map((m) => m.id);
    const byId = await fetchAttachments("maker", ids, ["logo", "profile"]);
    return makerList.map((m) => ({ ...m, attachments: byId[m.id] ?? {} }));
  }

  // Maker detail no longer embeds product attachments — fetch product images in
  // bulk and merge them back onto each product inside its category.
  async function hydrateProducts<T extends { products?: Array<{ id: string | number }> }>(
    productCategories: T[],
  ): Promise<T[]> {
    const productIds = productCategories.flatMap((cat) => (cat.products ?? []).map((p) => p.id));
    if (!productIds.length) return productCategories;
    const byId = await fetchAttachments("product", productIds, ["mainImage"]);
    return productCategories.map((cat) => ({
      ...cat,
      products: (cat.products ?? []).map((p) => ({
        ...p,
        attachments: byId[p.id] ?? {},
      })),
    }));
  }

  async function hydrateMakerAttachments(categories: MakerCategory[]): Promise<MakerCategory[]> {
    // Extract all makers from all categories
    const allMakers = categories.flatMap((cat) => cat.makers || []);

    // Hydrate them once (reuses hydrateMakers logic)
    const hydratedMakers = await hydrateMakers(allMakers);

    // Create lookup map
    const byId = new Map(hydratedMakers.map((m) => [m.id, m]));

    // Rebuild categories with hydrated makers
    return categories.map((cat) => ({
      ...cat,
      makers: (cat.makers || []).map((m) => byId.get(m.id) || m),
    }));
  }

  /**
   * Load the full active/biz category list.
   *
   * `makerCategoriesLoading` starts `true` and pages gate their whole body on
   * it, so this is the only thing that can ever clear it — hence the `finally`.
   * Clearing it in each branch by hand is how the search page came to hang
   * forever when a caller skipped this function altogether (see the note in
   * `pages/search/index.vue`); a `finally` at least guarantees that *reaching*
   * this function always releases the gate.
   *
   * Concurrent callers share one request. Without that, a page that awaits this
   * alongside another consumer fires the same heavy query twice.
   */
  async function getMakerCategories() {
    if (makerCategoriesRequest) return makerCategoriesRequest;

    makerCategoriesLoading.value = true;

    makerCategoriesRequest = (async () => {
      try {
        const response = await $fetch<{ models: MakerCategory[] }>(
          `${config.public.apiBase}/api/public/makerCategories?limit=9999&filter=${encodeURIComponent(
            JSON.stringify([
              { key: "is_active", value: 1 },
              { key: "biz", value: 1 },
            ]),
          )}`,
        );
        const hydratedCategories = await hydrateMakerAttachments(response.models);
        setMakerCategories(hydratedCategories);
      } catch {
        navigateTo("/404");
      } finally {
        makerCategoriesLoading.value = false;
        makerCategoriesRequest = null;
      }
    })();

    return makerCategoriesRequest;
  }

  async function getMakerCategory(name: string) {
    try {
      const response = await $fetch<{ models: MakerCategory[] }>(
        `${config.public.apiBase}/api/public/makerCategories/search?name=${name}&is_active=1`,
      );
      const raw = response.models[0];
      if (!raw) {
        chosenMakerCategory.value = false;
        return;
      }
      const hydrated = (await hydrateMakerAttachments([raw]))[0] ?? raw;
      chosenMakerCategory.value = { ...hydrated, slug: hydrated.slug || slugify(hydrated.name) };
    } catch {
      // Error
    }
  }

  async function getMakerCategoryBySlug(slug: string) {
    try {
      // Fetch all active biz categories and find by slug
      const response = await $fetch<{ models: MakerCategory[] }>(
        `${config.public.apiBase}/api/public/makerCategories?limit=9999&filter=${encodeURIComponent(
          JSON.stringify([
            { key: "is_active", value: 1 },
            { key: "biz", value: 1 },
          ]),
        )}`,
      );
      const userEmail = authStore.user?.email || "";
      const category = response.models
        .map((cat) => ({
          ...cat,
          slug: cat.slug || slugify(cat.name),
          // Sorted by name, NOT shuffled. This page is now server-rendered, and a
          // random order would emit different HTML for every request — defeating CDN
          // caching and producing an unstable ItemList order for crawlers. (The
          // homepage carousels still shuffle; that's client-only and fine.)
          makers: cat.makers
            .filter((m) => m.is_active !== false && isVisibleMaker(m.id, userEmail))
            // String()/?? guards a null name — `Maker.name` is typed non-null but
            // comes from an API response, and localeCompare on undefined throws.
            .sort((a, b) => String(a.name ?? "").localeCompare(String(b.name ?? ""))),
        }))
        .filter((cat) => cat.biz && cat.makers.length > 0)
        .find((cat) => cat.slug === slug);
      if (category) {
        const hydrated = (await hydrateMakerAttachments([category]))[0] ?? category;
        chosenMakerCategory.value = hydrated;
        return hydrated;
      }
      chosenMakerCategory.value = false;
      return false;
    } catch {
      chosenMakerCategory.value = false;
      return false;
    }
  }

  async function getMakerBySlug(
    payload: { slug: string; makerPage?: boolean },
    options?: { suppressRedirect?: boolean },
  ): Promise<MakerWithCategories | undefined> {
    makerLoading.value = true;

    try {
      const response = await $fetch<MakerWithCategories>(
        `${config.public.apiBase}/api/public/makers/slugs/${payload.slug}`,
      );
      // Inactive is definitive wherever we learn about it, so the cart is
      // dropped regardless of caller — same rule as the 404 branch below.
      // Only a maker page boots the customer away; a background refresh
      // (default layout cart refresh, /review, /success) leaves them put.
      // `suppressRedirect` must gate navigation too: SSR callers own the
      // response (navigating here AND letting them throw would emit two).
      if (response.is_active === false) {
        makerLoading.value = false;
        useOrdersStore().clearCartForUnavailableMaker(payload.slug);
        if (payload.makerPage && !options?.suppressRedirect) {
          navigateTo("/");
        }
        return;
      }

      // Maker detail returns products bare (no inline attachments). Hydrate
      // product mainImage in one bulk call before exposing the maker so every
      // consumer of getMakerBySlug (store/menu/cart pages) renders images.
      if (response.productCategories && Array.isArray(response.productCategories)) {
        response.productCategories = await hydrateProducts(
          response.productCategories as Array<{
            products?: Array<{ id: string | number }>;
          }>,
        );
      }

      chosenMaker.value = response;

      // Populate product categories from the maker response
      if (response.productCategories && Array.isArray(response.productCategories)) {
        const productsStore = useProductsStore();
        productsStore.setProductCategories(response.productCategories as any);
        productsStore.setBundles(response.productCategories as any);
      }

      makerLoading.value = false;
      // Return the maker so callers can use the resolved value directly rather
      // than reading the shared chosenMaker ref (which other callers — e.g. the
      // default layout's cart refresh — may overwrite in a concurrent request).
      return response;
    } catch (error) {
      makerLoading.value = false;

      // A 404 here is definitive: the public makers endpoint filters out
      // inactive and archived makers, so a deactivated one surfaces as a 404.
      // If the cart points at it, drop the cart — otherwise the customer lands
      // on a dead cart every visit with no way to clear it. Any other error
      // (offline / 5xx / timeout) is transient, so the cart is left alone and
      // we fall through to the usual redirect.
      if (
        getErrorStatus(error) === 404 &&
        useOrdersStore().clearCartForUnavailableMaker(payload.slug)
      ) {
        // Only send them away when this call was rendering that maker's page.
        // A background refresh (the default layout's cart refresh, /review,
        // /success) must not boot the customer off what they were doing.
        // SSR callers with suppressRedirect own the response themselves.
        if (payload.makerPage && !options?.suppressRedirect) {
          navigateTo("/");
        }
        return;
      }

      if (!options?.suppressRedirect) {
        navigateTo("/404");
      }
    }
  }

  // For group orders the visible menu is the org's frozen, drift-rechecked
  // customisation — NOT the maker's full catalog. Fetch it and override the
  // product categories getMakerBySlug populated. The backend is the sole authority
  // on visibility (CLAUDE.md: the frontend never re-derives menu state); we only
  // swap the data source.
  //
  // THREE outcomes, not a boolean, because two of them mean "keep the full catalogue" and
  // one of those is safe while the other is not:
  //
  //   "applied"      — the frozen menu is now in the products store.
  //   "uncustomised" — the order genuinely has no customisation (legacy group order created
  //                    before the feature, or non-group). The full catalogue IS the answer.
  //   "unavailable"  — we could not find out. A read failure, or no token to ask with.
  //
  // A boolean collapsed the last two, and the caller could only read "false". A transient 500
  // therefore left an employee browsing the maker's FULL catalogue on a customised Group
  // Select order — every dish their manager removed for an allergen back on screen, with the
  // only downstream signal being the orderability check that fires after they add one. On a
  // menu whose whole purpose is allergen exclusion, failing open is the wrong direction; the
  // caller has to be able to tell the difference and block.
  //
  // Same shape as the resolved-flag rule in CLAUDE.md (Backend-Rejectable State, rule 3): a
  // single falsy value cannot represent "resolved: no" and "not resolved" at once.
  async function applyOrderMenuCustomisation(
    orderId: string | number,
  ): Promise<"applied" | "uncustomised" | "unavailable"> {
    const { authFetch, hasToken } = useAuthenticatedFetch();
    // Not "uncustomised": with no token we never asked, so we do not know. This page is
    // behind auth middleware, so it is an unresolved state rather than a normal one.
    if (!hasToken()) return "unavailable";
    try {
      const response = await authFetch<{
        customised: boolean;
        productCategories: Array<{ products?: Array<{ id: string | number }> }>;
      }>(`/api/customers/orders/${orderId}/menu`);

      if (!response.customised) return "uncustomised";

      const hydrated = await hydrateProducts(response.productCategories);
      const productsStore = useProductsStore();
      productsStore.setProductCategories(hydrated as any);
      return "applied";
    } catch (error: unknown) {
      handleInvalidToken(error);
      return "unavailable";
    }
  }

  // The "View menu" preview a manager opens from change-maker has to show what their
  // TEAM will see, not the maker's full catalogue — otherwise they sign off on a menu
  // containing dishes their own org rules hide. Reads the annotated catalogue and keeps
  // only what the backend marked visible; the verdict is the server's (D5), we just
  // project it. Returns false when the preview could not be applied, so the caller can
  // say so rather than pass the uncurated catalogue off as the team's menu.
  //
  // `orderId` must be passed whenever the previewed maker is the one an existing order
  // already uses. Without it the endpoint evaluates against the ORGANISATION's current
  // defaults, but copy-on-create means a live order carries its own frozen ruleset — so the
  // preview showed verdicts for a menu that order doesn't have, and missed the per-dish
  // choices (hidden-by-hand, force-shown) the backend echoes back only in re-curate mode.
  async function applyCurationMenuPreview(
    makerId: string | number,
    orderId?: string | number | null,
  ): Promise<boolean> {
    const { authFetch, hasToken } = useAuthenticatedFetch();
    if (!hasToken()) return false;
    try {
      const query = orderId == null ? "" : `?orderId=${encodeURIComponent(String(orderId))}`;
      const response = await authFetch<CurationMenuResponse>(
        `/api/customers/makers/${makerId}/menu${query}`,
      );

      const visibleCategories = response.productCategories
        .map((category) => ({
          ...category,
          products: category.products.filter((product) => product.visibleToEmployees),
        }))
        .filter((category) => category.products.length > 0);

      const hydrated = await hydrateProducts(visibleCategories);
      useProductsStore().setProductCategories(hydrated as any);
      return true;
    } catch (error: unknown) {
      handleInvalidToken(error);
      return false;
    }
  }

  async function getMakerById(id: string | number) {
    makerLoading.value = true;

    try {
      const response = await $fetch<Maker>(`${config.public.apiBase}/api/public/makers/${id}`);
      chosenMaker.value = response;
      makerLoading.value = false;
      return response;
    } catch {
      makerLoading.value = false;
    }
  }

  async function getAvailableMakers(date?: string) {
    if (!date) {
      setAvailableMakers(false);
      return;
    }
    try {
      const response = await $fetch<(string | number)[]>(
        `${config.public.apiBase}/api/public/makers/availableMaker?date=${date}`,
      );
      setAvailableMakers(response);
    } catch {
      // Error
    }
  }

  async function getDeliveryDays(id?: string | number) {
    if (!id) return;
    try {
      const response = await $fetch<unknown[]>(
        `${config.public.apiBase}/api/public/makers/deliveryDays/${id}`,
      );
      makerDeliveryDays.value = response;
    } catch {
      // Error
    }
  }

  async function getPackages(payload?: { filters?: unknown[] }) {
    packagesLoading.value = true;
    const filters = payload?.filters || [];
    const filterArray = [["is_active", true, true], ...(filters as Array<unknown[]>)];
    let filterQuery = "";
    if (filterArray.length) {
      filterQuery =
        "&filter=" +
        encodeURIComponent(
          JSON.stringify(filterArray.map(([key, value, exact]) => ({ key, value, exact }))),
        );
    }

    try {
      const response = await $fetch<{ packages: Record<string, unknown>[] }>(
        `${config.public.apiBase}/api/public/packages/?limit=999${filterQuery}`,
      );
      const userEmail = authStore.user?.email || "";
      packages.value = response.packages.filter((pkg) =>
        isVisibleMaker(pkg.maker_id as string, userEmail),
      );
      packagesLoading.value = false;
    } catch {
      packagesLoading.value = false;
    }
  }

  async function getPackageById(id: string) {
    try {
      const response = await $fetch<Record<string, unknown>>(
        `${config.public.apiBase}/api/public/packages/${id}`,
      );
      chosenPackage.value = response;
    } catch {
      // Error
    }
  }

  async function getPackageTypes() {
    try {
      const response = await $fetch<unknown[]>(
        `${config.public.apiBase}/api/public/subConfig/packageTypes`,
      );
      packageTypes.value = response;
    } catch {
      // Error
    }
  }

  async function getCustomerPackageStructure() {
    if (!authStore.token?.token) return;
    const { authFetch } = useAuthenticatedFetch();

    try {
      const response = await authFetch("/api/customers/subConfig");
      orgPackageChoices.value = response;
    } catch {
      // Error
    }
  }

  async function getDefaultSubscriptionConfigurations(id: string) {
    try {
      const response = await $fetch(`${config.public.apiBase}/api/public/subConfig/${id}`);
      defaultPackageChoices.value = response;
    } catch {
      // Error
    }
  }

  async function deepSearch(search: string) {
    if (!search || search.length <= 2) {
      filteredMakerCategories.value = false;
      searchResultMakers.value = [];
      return;
    }

    const uiStore = useUiStore();
    // Only block with the full-screen overlay when there's nothing to show yet.
    // If results are already pre-populated (e.g. from SearchBar on Enter),
    // the request resolves silently in the background.
    const hasPreloadedResults =
      Array.isArray(filteredMakerCategories.value) && filteredMakerCategories.value.length > 0;
    if (!hasPreloadedResults) uiStore.setFilterLoading(true);

    try {
      // Shared logic (abort-safe fetch; backend-authoritative, with a local
      // word-prefix fallback only on request failure) lives in useDeepSearch;
      // a null result means a newer search superseded this one, so leave the
      // current results untouched.
      const results = await runDeepSearch(search);
      if (results !== null) {
        // Keep searchResultMakers as the source of truth for the active search so
        // any filter applied afterwards refines these makers (empty = inactive).
        searchResultMakers.value = results;
        filteredMakerCategories.value = results.length ? results : false;
      }
    } finally {
      uiStore.setFilterLoading(false);
    }
  }

  // Raw deepFilter fetch: returns the matched maker ids without touching any
  // search-page state, so pages other than /search (e.g. premium change-maker)
  // can intersect the ids against their own maker lists. Null on empty payload
  // or fetch error.
  async function fetchDeepFilterIds(payload: {
    tags?: { id: string | number }[];
    cuisines?: { id: string | number }[];
  }): Promise<DeepFilterResponse | null> {
    const tagsParam =
      payload.tags && payload.tags.length > 0
        ? `&tags=${encodeURIComponent(JSON.stringify(payload.tags.map((t) => t.id)))}`
        : "";
    const cuisinesParam =
      payload.cuisines && payload.cuisines.length > 0
        ? `&cuisines=${encodeURIComponent(JSON.stringify(payload.cuisines.map((c) => c.id)))}`
        : "";

    if (!tagsParam && !cuisinesParam) return null;

    try {
      return await $fetch<DeepFilterResponse>(
        `${config.public.apiBase}/api/public/makers/deepFilter?is_active=1${tagsParam}${cuisinesParam}`,
      );
    } catch {
      return null;
    }
  }

  const deepFilter = debounce(
    async (
      payload:
        | {
            tags?: { id: string | number }[];
            cuisines?: { id: string | number }[];
          }
        | false,
    ) => {
      const uiStore = useUiStore();

      if (!payload || (!payload.tags?.length && !payload.cuisines?.length)) {
        setFilteredMakers(false);
        uiStore.setFilterLoading(false);
        return;
      }

      uiStore.setFilterLoading(true);

      try {
        const response = await fetchDeepFilterIds(payload);
        // Null means the fetch failed — leave the current results untouched,
        // matching the previous empty-catch behaviour.
        if (response) {
          setFilteredMakers(response, !!payload.cuisines?.length);
        }
      } finally {
        uiStore.setFilterLoading(false);
      }
    },
    200,
  );

  async function getServerTime() {
    try {
      return await $fetch<string>(`${config.public.apiBase}/api/public/serverTime`);
    } catch {
      return null;
    }
  }

  return {
    // State
    makers,
    makersMap,
    makerCategories,
    chosenMaker,
    chosenMakerCategory,
    makersLoading,
    makerCategoriesLoading,
    makerLoading,
    makerDeliveryDays,
    makerIdsByDate,
    validSubscriptionMakers,
    filteredMakerCategories,
    searchResultMakers,
    chosenPackage,
    packages,
    packagesLoading,
    packageTypes,
    orgPackageChoices,
    defaultPackageChoices,

    // Getters
    openMakers,
    closedMakers,

    // Setters
    setMakersLoading,
    setMakerLoading,
    setMakers,
    setMakerCategories,
    setFilteredMakers,
    setAvailableMakers,
    setValidSubscriptionMakers,
    setChosenPackage,

    // Actions
    getMakers,
    getMakerCategories,
    getMakerCategory,
    getMakerCategoryBySlug,
    getMakerBySlug,
    applyOrderMenuCustomisation,
    applyCurationMenuPreview,
    getMakerById,
    getAvailableMakers,
    getDeliveryDays,
    getPackages,
    getPackageById,
    getPackageTypes,
    getCustomerPackageStructure,
    getDefaultSubscriptionConfigurations,
    deepSearch,
    deepFilter,
    fetchDeepFilterIds,
    getServerTime,
  };
});
